import { ENQUADRAMENTOS, NOME_ENQUADRAMENTO, type Enquadramento } from '@social/shared';
import { Campo, Entrada, Selecao } from '../componentes/ui';

/*
  `| undefined` explícito em cada campo.

  O projeto roda com `exactOptionalPropertyTypes`, que separa "ausente" de
  "presente e indefinido". Limpar um campo do filtro atribui `undefined` — e
  sem esta declaração o TypeScript recusa, com razão: seria o único jeito de
  "apagar" um filtro sem reconstruir o objeto inteiro.
*/
export interface FiltrosAvancados {
  enquadramento?: Enquadramento | undefined;
  cnpj?: string | undefined;
  nire?: string | undefined;
  municipio?: string | undefined;
  uf?: string | undefined;
  socio?: string | undefined;
  capitalDe?: string | undefined;
  capitalAte?: string | undefined;
  semRegistro?: 'sim' | 'nao' | undefined;
}

export const SEM_FILTRO: FiltrosAvancados = {};

/**
 * Os campos da gaveta de filtros avançados da consulta de sociedades.
 *
 * Vive em arquivo próprio e não dentro de `sociedades.tsx` por dois motivos,
 * nessa ordem: a tela passou do teto de 600 linhas do lint, e esses nove
 * campos são a parte dela que mais cresce — cada filtro novo entra aqui, não
 * na barra (ver a regra "barra vs. gaveta" em `pagina-shell.tsx`).
 *
 * É uma função e não um componente: `GavetaFiltros` recebe uma função de
 * renderização, porque o rascunho só existe enquanto a gaveta está aberta e
 * não é aplicado até a pessoa confirmar. Passar isto como componente exigiria
 * repassar `rascunho` e `mexer` como props, sem nada em troca.
 *
 * Cada campo aqui ocupa a LARGURA INTEIRA da gaveta — ver o porquê no
 * comentário da grade em `gaveta-filtros.tsx`.
 */
export function camposDeFiltroDeSociedade(
  rascunho: FiltrosAvancados,
  mexer: (parcial: Partial<FiltrosAvancados>) => void,
) {
  return (
    <>
      <Campo rotulo="Enquadramento" id="g-enquadramento">
        <Selecao
          id="g-enquadramento"
          value={rascunho.enquadramento ?? ''}
          onChange={(e) =>
            mexer({
              enquadramento: (e.target.value || undefined) as Enquadramento | undefined,
            })
          }
        >
          <option value="">Todos</option>
          {ENQUADRAMENTOS.map((opcao) => (
            <option key={opcao} value={opcao}>
              {NOME_ENQUADRAMENTO[opcao]}
            </option>
          ))}
        </Selecao>
      </Campo>

      <Campo rotulo="CNPJ" id="g-cnpj">
        <Entrada
          id="g-cnpj"
          value={rascunho.cnpj ?? ''}
          onChange={(e) => mexer({ cnpj: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="NIRE" id="g-nire">
        <Entrada
          id="g-nire"
          value={rascunho.nire ?? ''}
          onChange={(e) => mexer({ nire: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="Município" id="g-municipio">
        <Entrada
          id="g-municipio"
          value={rascunho.municipio ?? ''}
          onChange={(e) => mexer({ municipio: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="UF" id="g-uf">
        <Entrada
          id="g-uf"
          maxLength={2}
          value={rascunho.uf ?? ''}
          onChange={(e) => mexer({ uf: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="Nome do sócio" id="g-socio">
        <Entrada
          id="g-socio"
          value={rascunho.socio ?? ''}
          onChange={(e) => mexer({ socio: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="Capital de (R$)" id="g-capital-de">
        <Entrada
          id="g-capital-de"
          type="number"
          min="0"
          step="0.01"
          value={rascunho.capitalDe ?? ''}
          onChange={(e) => mexer({ capitalDe: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="Capital até (R$)" id="g-capital-ate">
        <Entrada
          id="g-capital-ate"
          type="number"
          min="0"
          step="0.01"
          value={rascunho.capitalAte ?? ''}
          onChange={(e) => mexer({ capitalAte: e.target.value || undefined })}
        />
      </Campo>
      <Campo rotulo="Sem registro na Junta" id="g-sem-registro">
        <Selecao
          id="g-sem-registro"
          value={rascunho.semRegistro ?? ''}
          onChange={(e) =>
            mexer({
              semRegistro: (e.target.value || undefined) as 'sim' | 'nao' | undefined,
            })
          }
        >
          <option value="">Todos</option>
          <option value="sim">Só as sem CNPJ</option>
          <option value="nao">Só as com CNPJ</option>
        </Selecao>
      </Campo>
    </>
  );
}
